import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { Box } from '@mui/material';
import { ItemsListFC } from '@provider-portal/menu-common/components/ItemsList/ItemsListFC';
import _ from 'lodash';
import { createPromotionsApiFetchers } from 'promotions';
import { ListItemElement, useMenuItemsLoader } from 'promotions/hooks/useMenuItemsLoader';

import { useConstant } from 'hooks/useConstant';
import { useSelectedProvider } from 'hooks/useSelectedProvider';

import { useAppServices } from 'components/AppServicesProvider';
import { SelectedMenuItem } from 'root/promotions/types';

import { MenuItemsUpdateInfo } from '../../../../../common/all/api/gen/CampaignDeliveryProvider';

interface MenuItemSelectorProps {
  exclusionTags: string[];
  savedMenuItemProductIds: string[];
  onItemListToggle: () => void;
  onMenuItemsUpdate: (newItemsToUpdate: MenuItemsUpdateInfo[]) => void;
}

export const SavedMenuItemSelector: React.FC<MenuItemSelectorProps> = ({
  exclusionTags,
  savedMenuItemProductIds,
  onItemListToggle,
  onMenuItemsUpdate,
}) => {
  const { apiClient } = useAppServices();
  const { fetchers } = useConstant(() => ({
    fetchers: createPromotionsApiFetchers(apiClient),
  }));
  const intl = useIntl();
  const selectedProvider = useSelectedProvider();
  const exclusionMessage = useMemo(() => {
    return intl.formatMessage({ id: 'components.AddPromotion.exclusionTooltipMessage' });
  }, [intl]);

  const menuItemsList: ListItemElement[] = useMenuItemsLoader(
    selectedProvider.providerId,
    fetchers.getMenuItemsForProviderActiveMenu.useFetcher(),
    exclusionTags,
    exclusionMessage,
  );

  const strings = useMemo(
    () => ({
      components: {
        addItems: {
          listComponent: {
            addLabel: intl.formatMessage({ id: 'components.ModifierAddEdit.addItems' }),
          },
          viewMore: (num: number) =>
            intl.formatMessage({ id: 'components.ModifierAddEdit.viewMore' }, { count: num }),
          dialogComponent: {
            cardTitle: intl.formatMessage({ id: 'components.ModifierAddEdit.cardTitle' }),
            searchLabel: intl.formatMessage({ id: 'components.ModifierAddEdit.searchLabel' }),
            noSearchResultsTitle: intl.formatMessage({
              id: 'components.ModifierAddEdit.noSearchResultsTitle',
            }),
            noSearchResultsSubtitle: intl.formatMessage({
              id: 'components.ModifierAddEdit.noSearchResultsSubtitle',
            }),
            cancel: intl.formatMessage({ id: 'buttons.cancel' }),
            addItems: intl.formatMessage({ id: 'buttons.addItems' }),
          },
        },
      },
    }),
    [intl],
  );

  const menuItemMap = useMemo(() => {
    return menuItemsList.reduce((acc, item) => {
      acc[item.id] = {
        globalStableContextId: item.id,
        menuItemName: item.name,
        price: item.priceDetails.price,
        currency: item.priceDetails.currency,
        productId: item.product_id,
      };
      // Creating map for both stableContextId and product_id for backward compatibility
      acc[item.product_id] = {
        globalStableContextId: item.id,
        menuItemName: item.name,
        price: item.priceDetails.price,
        currency: item.priceDetails.currency,
        productId: item.product_id,
      };
      return acc;
    }, {} as Record<string, SelectedMenuItem>);
  }, [menuItemsList]);

  const savedStableContextIds = useMemo(() => {
    return _.compact(
      savedMenuItemProductIds.map((productId) => menuItemMap[productId]?.globalStableContextId),
    );
  }, [savedMenuItemProductIds, menuItemMap]);

  const onItemsSelectHandler = useCallback(
    (stableContextIds: string[]) => {
      const newItems: MenuItemsUpdateInfo[] = (
        Object.keys(menuItemMap).length > 0 ? stableContextIds : []
      ).map((stableContextId) => {
        return {
          global_stable_context_id: menuItemMap[stableContextId].globalStableContextId,
          name: menuItemMap[stableContextId].menuItemName,
          price: menuItemMap[stableContextId].price,
          currency: menuItemMap[stableContextId].currency,
          product_id: menuItemMap[stableContextId].productId,
        };
      });
      onItemListToggle();
      onMenuItemsUpdate(newItems);
    },
    [onItemListToggle, onMenuItemsUpdate, menuItemMap],
  );

  const onItemsToggle = useCallback(() => {
    onItemListToggle();
  }, [onItemListToggle]);

  return (
    <Box mb={2}>
      <ItemsListFC
        strings={strings.components.addItems}
        selectedItems={savedStableContextIds}
        itemsList={menuItemsList}
        onSelectedValuesChange={onItemsSelectHandler}
        onItemListToggle={onItemsToggle}
        shouldShowImages
        isBulkTagsEditMode
        isDefaultDialogOpen
        indeterminateTags={[]}
      />
    </Box>
  );
};
